import React, { useEffect, useState, useCallback } from 'react';
import {
    Table, Button, Form, Input, message, Card, Row, Col, Tag, Modal, Switch, Segmented,
    Upload, Typography, Space
} from 'antd';
import { ExperimentOutlined, EditOutlined, PaperClipOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { labService } from '../../services';
import AttachmentPreview from '../../components/AttachmentPreview';
import LabComponentFields from '../../components/LabComponentFields';
import { ATTACHMENT_ACCEPT, attachmentsOf } from '../../utils/attachments';

const { Text } = Typography;

const STATUS_COLORS = { ordered: 'orange', pending: 'gold', completed: 'green', cancelled: 'default' };

const LabTests = () => {
    const { t } = useTranslation();
    const [view, setView] = useState('pending'); // 'pending' | 'completed'
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [current, setCurrent] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    const fetchTests = useCallback(async () => {
        setLoading(true);
        try {
            // 'pending' view leaves status unset -> backend returns ordered+pending
            const status = view === 'completed' ? 'completed' : undefined;
            const res = await labService.getIncomingTests(status);
            setTests(res.data || []);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    }, [view, t]);

    useEffect(() => {
        fetchTests();
    }, [fetchTests]);

    const openResult = (record) => {
        setCurrent(record);
        setFileList([]);
        form.setFieldsValue({
            resultValue: record.resultValue,
            referenceRange: record.referenceRange,
            isAbnormal: record.isAbnormal || false,
            notes: record.notes,
            // The analytes were listed on the order, so a panel opens onto its own
            // named boxes - Hb, WBC, Platelets - instead of one field the whole
            // CBC has to be typed into. Copied rather than referenced so editing
            // the form does not mutate the row behind the table.
            components: (record.components || []).map((component) => ({ ...component }))
        });
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const newFiles = fileList.map((f) => f.originFileObj).filter(Boolean);
            const alreadyAttached = attachmentsOf(current).length > 0;

            // The whole point of attaching the result sheet is that the value need
            // not be retyped — but one of the three has to be there. A panel filled
            // in analyte by analyte is a result like any other.
            const hasComponentValues = (values.components || [])
                .some((component) => String(component?.value || '').trim());

            if (!values.resultValue && newFiles.length === 0 && !alreadyAttached && !hasComponentValues) {
                message.error(t('enterResultOrAttach') || 'Enter a result or attach the result file');
                return;
            }

            setSubmitting(true);
            let attachmentUrl = [];
            if (newFiles.length > 0) {
                const uploaded = await labService.uploadResultFiles(newFiles);
                attachmentUrl = uploaded.data || [];
            }

            await labService.submitResult(current._id, { ...values, attachmentUrl });
            message.success(t('resultSaved') || 'Result saved');
            setModalOpen(false);
            setFileList([]);
            fetchTests();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setSubmitting(false);
        }
    };

    const columns = [
        {
            title: t('patient') || 'Patient',
            key: 'patient',
            render: (_, r) => (
                <div>
                    <div>{r.patient?.name || '-'}</div>
                    <small style={{ color: '#888' }}>{r.patient?.phoneNumber || ''}</small>
                </div>
            )
        },
        { title: t('doctor') || 'Doctor', key: 'doctor', render: (_, r) => r.doctor?.name || '-' },
        { title: t('testType') || 'Type', dataIndex: 'testType', key: 'testType', render: (v) => <Tag>{v}</Tag> },
        { title: t('testName') || 'Test', dataIndex: 'testName', key: 'testName' },
        {
            title: t('orderDate') || 'Ordered',
            dataIndex: 'orderDate',
            key: 'orderDate',
            render: (v) => v ? new Date(v).toLocaleDateString() : '-'
        },
        {
            title: t('status') || 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (s) => <Tag color={STATUS_COLORS[s] || 'default'}>{t(s) || s}</Tag>
        },
        {
            title: t('result') || 'Result',
            key: 'result',
            render: (_, r) => {
                const filled = (r.components || []).filter((c) => String(c.value || '').trim());
                if (filled.length > 0) {
                    // The first few analytes, so the queue shows what came back
                    // without opening the row.
                    return (
                        <Space size={4} wrap>
                            {filled.slice(0, 3).map((c) => (
                                <Tag key={c.code} color={c.isAbnormal ? 'red' : 'default'}>
                                    {c.name}: {c.value}{c.unit ? ` ${c.unit}` : ''}
                                </Tag>
                            ))}
                            {filled.length > 3 && <Tag>+{filled.length - 3}</Tag>}
                        </Space>
                    );
                }
                if (r.resultValue) {
                    return <span>{r.resultValue} {r.isAbnormal && <Tag color="red">{t('abnormal') || 'Abnormal'}</Tag>}</span>;
                }
                return attachmentsOf(r).length > 0
                    ? <Tag color="blue">{t('seeAttachment') || 'See attachment'}</Tag>
                    : '-';
            }
        },
        {
            title: t('attachments') || 'Attachments',
            key: 'attachments',
            render: (_, r) => <AttachmentPreview files={attachmentsOf(r)} />
        },
        {
            title: t('actions') || 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button type="primary" icon={<EditOutlined />} onClick={() => openResult(record)}>
                    {record.status === 'completed' ? (t('editResult') || 'Edit Result') : (t('enterResult') || 'Enter Result')}
                </Button>
            )
        }
    ];

    const existingFiles = attachmentsOf(current);

    return (
        <div className="page-container">
            <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
                <Col><h1 style={{ margin: 0 }}><ExperimentOutlined /> {t('labTests') || 'Lab Tests'}</h1></Col>
                <Col>
                    <Segmented
                        value={view}
                        onChange={setView}
                        options={[
                            { label: t('pending') || 'Pending', value: 'pending' },
                            { label: t('completed') || 'Completed', value: 'completed' }
                        ]}
                    />
                </Col>
            </Row>

            <Card>
                <Table
                    columns={columns}
                    dataSource={tests}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 1100 }}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            <Modal
                title={`${t('enterResult') || 'Enter Result'} — ${current?.testName || ''}`}
                open={modalOpen}
                onOk={handleSubmit}
                onCancel={() => setModalOpen(false)}
                confirmLoading={submitting}
                okText={t('save') || 'Save'}
                width="min(900px, 94vw)"
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    {/* A panel's own named boxes come first: for a CBC this is the
                        whole result, and the free-text field below is then just the
                        comment. A single-analyte test has no components and the
                        section renders nothing. */}
                    <LabComponentFields form={form} listName={['components']} />

                    <Form.Item
                        name="resultValue"
                        label={t('resultValue') || 'Result'}
                        extra={t('resultOptionalWithAttachment') || 'Optional if you attach the result sheet'}
                    >
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item name="referenceRange" label={t('referenceRange') || 'Reference Range'}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="isAbnormal" label={t('abnormal') || 'Abnormal'} valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        label={t('resultAttachments') || 'Result Attachments'}
                        extra={t('resultAttachmentsHint') || 'Photo or PDF of the result sheet — the doctor sees it as it is.'}
                    >
                        {existingFiles.length > 0 && (
                            <div style={{ marginBottom: 8 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    <PaperClipOutlined /> {t('alreadyAttached') || 'Already attached'}
                                </Text>
                                <div style={{ marginTop: 4 }}>
                                    <AttachmentPreview files={existingFiles} size={64} />
                                </div>
                            </div>
                        )}
                        <Upload
                            listType="picture-card"
                            fileList={fileList}
                            onChange={({ fileList: newList }) => setFileList(newList)}
                            beforeUpload={() => false}
                            accept={ATTACHMENT_ACCEPT}
                            multiple
                        >
                            {fileList.length >= 10 ? null : (
                                <div>
                                    <PaperClipOutlined style={{ fontSize: 20 }} />
                                    <div style={{ marginTop: 8 }}>{t('upload') || 'Upload'}</div>
                                </div>
                            )}
                        </Upload>
                    </Form.Item>

                    <Form.Item name="notes" label={t('notes') || 'Notes'}>
                        <Input.TextArea rows={2} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default LabTests;
